/** EmptyState / ErrorState (§1.2 "No dead ends"): human explanation + a next action, always. */
import type { ReactNode } from 'react';
import { cn } from '../cn';

export interface StateProps {
  title: ReactNode;
  /** Plain-language explanation of what happened / why there is nothing here. */
  body: ReactNode;
  /** The next action (a Button or link). Required so no state is a dead end. */
  action: ReactNode;
  secondaryAction?: ReactNode;
  icon?: ReactNode;
  /** Reference an operator can use (support case id, request id). Never a token or secret. */
  reference?: string;
  className?: string;
}

function StateBox({
  tone,
  title,
  body,
  action,
  secondaryAction,
  icon,
  reference,
  className,
}: StateProps & { tone: 'empty' | 'error' }) {
  return (
    <section
      className={cn('wd-state', className)}
      data-tone={tone}
      role={tone === 'error' ? 'alert' : undefined}
    >
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      <h2 className="wd-state__title">{title}</h2>
      <p className="wd-state__body">{body}</p>
      {reference ? (
        <p className="wd-state__body">
          Reference: <code>{reference}</code>
        </p>
      ) : null}
      <div className="wd-btn-group">
        {action}
        {secondaryAction}
      </div>
    </section>
  );
}

export function EmptyState(props: StateProps) {
  return <StateBox tone="empty" {...props} />;
}

export function ErrorState(props: StateProps) {
  return <StateBox tone="error" {...props} />;
}
